import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Checkbox
} from "@material-ui/core";
import { formatMoney } from "../lithic/Money";
import Link from "./Link";
import dayjs from "dayjs";

function formatPeriod(period) {
  if (!period) return "-";
  return dayjs.utc(period.start).format("MMM YYYY");
}

function formatBankLast4(bankAccount) {
  if (!bankAccount?.last4) return "-";
  return "****" + bankAccount.last4;
}

function formatAmountInGroup(stmtAmount) {
  if (!stmtAmount) return "-";
  return "$" + (stmtAmount.amount || 0).toFixed(2);
}

function formatTotal(total) {
  if (!total) return "-";
  return formatMoney(total);
}

function formatPayType(payType) {
  return payType === "pay_in" ? "Invoice" : "Payout";
}

function StatementRow({ stmt, selectable, selectedIds, onSelect, linkStatements, showAmountInGroup, statementAmounts }) {
  const isSelected = selectable && selectedIds.includes(stmt.id);
  const stmtAmount = showAmountInGroup
    ? statementAmounts.find((sa) => sa.statementId === stmt.id)
    : null;
  const handleClick = selectable ? () => onSelect(stmt.id) : undefined;
  const rowStyle = selectable ? { cursor: "pointer" } : undefined;
  const idCell = linkStatements
    ? <Link to={"/statements/" + stmt.id}>{stmt.id}</Link>
    : stmt.id;

  return (
    <TableRow hover={selectable} onClick={handleClick} selected={isSelected} style={rowStyle}>
      {selectable && (
        <TableCell padding="checkbox">
          <Checkbox checked={isSelected} />
        </TableCell>
      )}
      <TableCell>{idCell}</TableCell>
      <TableCell>{stmt.partnerName || "-"}</TableCell>
      <TableCell>{stmt.propertyName || "-"}</TableCell>
      <TableCell>{formatPeriod(stmt.period)}</TableCell>
      <TableCell>{formatPayType(stmt.payType)}</TableCell>
      <TableCell>{stmt.status || "-"}</TableCell>
      <TableCell>{formatBankLast4(stmt.bankAccount)}</TableCell>
      {showAmountInGroup && (
        <TableCell>{formatAmountInGroup(stmtAmount)}</TableCell>
      )}
      <TableCell>{formatTotal(stmt.total)}</TableCell>
      <TableCell>{stmt.billingPeriod || "-"}</TableCell>
    </TableRow>
  );
}

/**
 * Shared statements table used by both the aggregated payment creation page
 * and the aggregated payment detail page.
 *
 * Props:
 *  - statements: array of statement objects
 *  - selectable: boolean, show checkboxes for selection
 *  - selectedIds: array of selected statement IDs (when selectable)
 *  - onSelect: (id) => void, toggle a single row (when selectable)
 *  - onSelectAll: (event) => void, toggle all rows (when selectable)
 *  - statementAmounts: optional array of {statementId, amount} for "Amount in Group" column
 *  - maxHeight: optional max height for scroll container
 *  - linkStatements: boolean, make statement IDs clickable links (default false)
 */
export default function StatementsTable({
  statements = [],
  selectable = false,
  selectedIds = [],
  onSelect,
  onSelectAll,
  statementAmounts,
  maxHeight = 500,
  linkStatements = false
}) {
  const showAmountInGroup = !!statementAmounts;

  return (
    <TableContainer component={Paper} style={{ maxHeight, overflow: "auto" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    selectedIds.length === statements.length &&
                    statements.length > 0
                  }
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < statements.length
                  }
                  onChange={onSelectAll}
                />
              </TableCell>
            )}
            <TableCell>ID</TableCell>
            <TableCell>Partner</TableCell>
            <TableCell>Property</TableCell>
            <TableCell>Period</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Bank Account</TableCell>
            {showAmountInGroup && <TableCell>Amount in Group</TableCell>}
            <TableCell>Total</TableCell>
            <TableCell>Billing Period</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statements.map((stmt) => (
            <StatementRow
              key={stmt.id}
              stmt={stmt}
              selectable={selectable}
              selectedIds={selectedIds}
              onSelect={onSelect}
              linkStatements={linkStatements}
              showAmountInGroup={showAmountInGroup}
              statementAmounts={statementAmounts}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
